import React, { useEffect, useRef } from 'react';
import { Box, FormControl, MenuItem, Pagination, Select, Typography } from '@mui/material';

interface UniversalPaginationProps {
  /** Поточна сторінка (1-indexed) */
  page: number;
  /** Загальна кількість сторінок */
  count: number;
  /** Поточна кількість елементів на сторінці */
  limit: number;
  /** Загальна кількість записів — якщо передано, відображається "X–Y з Z" */
  total?: number;
  /** Доступні варіанти розміру сторінки */
  limitOptions?: number[];
  /** Callback при зміні сторінки */
  onPageChange: (page: number) => void;
  /** Callback при зміні розміру сторінки — якщо не передано, селектор розміру приховується */
  onLimitChange?: (limit: number) => void;
  /**
   * Ключ для localStorage — якщо передано, компонент сам зберігає і відновлює
   * вибраний розмір сторінки (`storageKey`) і поточну позицію (`${storageKey}:page`) між візитами.
   */
  storageKey?: string;
  /** Не рендерити нічого якщо count <= 1 (default: true) */
  hideIfSinglePage?: boolean;
  disabled?: boolean;
  sx?: object;
}

const DEFAULT_LIMIT_OPTIONS = [10, 25, 50];

export const UniversalPagination: React.FC<UniversalPaginationProps> = ({
  page,
  count,
  limit,
  total,
  limitOptions = DEFAULT_LIMIT_OPTIONS,
  onPageChange,
  onLimitChange,
  storageKey,
  hideIfSinglePage = true,
  disabled,
  sx,
}) => {
  const onLimitChangeRef = useRef(onLimitChange);
  onLimitChangeRef.current = onLimitChange;

  const onPageChangeRef = useRef(onPageChange);
  onPageChangeRef.current = onPageChange;

  const limitRef = useRef(limit);
  limitRef.current = limit;

  const pageRef = useRef(page);
  pageRef.current = page;

  const pageStorageKey = storageKey ? `${storageKey}:page` : undefined;

  useEffect(() => {
    if (!storageKey) return;
    try {
      if (onLimitChangeRef.current) {
        const rawLimit = localStorage.getItem(storageKey);
        const savedLimit = rawLimit ? Number(rawLimit) : NaN;
        if (!Number.isNaN(savedLimit) && savedLimit > 0 && savedLimit !== limitRef.current) {
          onLimitChangeRef.current(savedLimit);
        }
      }

      if (pageStorageKey) {
        const rawPage = localStorage.getItem(pageStorageKey);
        const savedPage = rawPage ? Number(rawPage) : NaN;
        if (!Number.isNaN(savedPage) && savedPage > 0 && savedPage !== pageRef.current) {
          onPageChangeRef.current(savedPage);
        }
      }
    } catch {
      // ігноруємо пошкоджений стан
    }
  }, [storageKey, pageStorageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLimitChange = (newLimit: number) => {
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, String(newLimit));
      } catch {
        // ігноруємо помилки запису
      }
    }
    onLimitChange?.(newLimit);
  };

  const handlePageChange = (newPage: number) => {
    if (pageStorageKey) {
      try {
        localStorage.setItem(pageStorageKey, String(newPage));
      } catch {
        // ігноруємо помилки запису
      }
    }
    onPageChange(newPage);
  };

  if (hideIfSinglePage && count <= 1 && !onLimitChange) return null;

  const from = total ? (page - 1) * limit + 1 : null;
  const to = total ? Math.min(page * limit, total) : null;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mt: 2,
        flexWrap: 'wrap',
        gap: 1,
        ...sx,
      }}
    >
      {count > 1 ? (
        <Pagination
          count={count}
          page={page}
          onChange={(_e, value) => handlePageChange(value)}
          color="primary"
          disabled={disabled}
        />
      ) : (
        <Box />
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {total != null && from != null && to != null && (
          <Typography variant="body2" color="text.secondary">
            {from}–{to} з {total}
          </Typography>
        )}

        {onLimitChange && (
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select
              value={limit}
              disabled={disabled}
              onChange={(e) => handleLimitChange(Number(e.target.value))}
            >
              {limitOptions.map((n) => (
                <MenuItem key={n} value={n}>
                  {n} / стор.
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
    </Box>
  );
};
